import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAlert } from '../CustomAlert/AlertContext';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../common/EmptyState';
import { PipelineBoardSkeleton } from '../common/Skeleton';

const STAGES = [
  { id: 'SCREENED', label: 'Screened Candidates', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.03)' },
  { id: 'SCHEDULED', label: 'Interview Scheduled', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.03)' },
  { id: 'EVALUATED', label: 'Evaluated / Cleared', color: '#10b981', bg: 'rgba(16, 185, 129, 0.03)' },
  { id: 'HIRED', label: 'Hired & Offer Sent', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.03)' }
];

export default function HiringPipeline() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { showAlert } = useAlert();

  // Extract unique jobs from candidates list dynamically
  const uniqueJobs = Array.from(
    new Set(
      candidates
        .map(c => JSON.stringify({ id: c.job_opening_id, title: c.job_title }))
        .filter(Boolean)
    )
  ).map(j => JSON.parse(j));

  const filteredCandidates = candidates.filter(cand => {
    // Search query filter
    const matchesSearch = 
      cand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cand.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (cand.skills_extracted && cand.skills_extracted.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (cand.job_title && cand.job_title.toLowerCase().includes(searchQuery.toLowerCase()));

    // Job filter
    const matchesJob = selectedJobId === 'all' || cand.job_opening_id === Number(selectedJobId);

    // Match tier filter
    let matchesTier = true;
    if (selectedTier === 'top') matchesTier = cand.matching_percentage >= 85;
    else if (selectedTier === 'strong') matchesTier = cand.matching_percentage >= 70 && cand.matching_percentage < 85;
    else if (selectedTier === 'weak') matchesTier = cand.matching_percentage < 70;

    return matchesSearch && matchesJob && matchesTier;
  });

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/screening/candidates/all');
      setCandidates(res.data);
    } catch (err) {
      console.error('Failed to fetch candidates for pipeline:', err);
      showAlert('Failed to load hiring pipeline.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  const moveCandidate = async (candidateId, newStatus) => {
    try {
      await api.put(`/api/screening/candidate/${candidateId}/status`, { status: newStatus });
      // Update local state dynamically for a smooth transition
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: newStatus } : c));
      showAlert('Candidate stage updated successfully.', 'success');
    } catch (err) {
      console.error('Failed to update stage:', err);
      showAlert('Failed to move candidate to next stage.', 'error');
    }
  };

  const getTierColor = (score) => {
    if (score >= 85) return { color: '#16a34a', bg: '#dcfce7' }; // Top Match
    if (score >= 70) return { color: '#2563eb', bg: '#dbeafe' }; // Strong Match
    return { color: '#4b5563', bg: '#f3f4f6' }; // Weak/Moderate Match
  };

  const renderColumn = (stage) => {
    const stageCandidates = filteredCandidates.filter(c => {
      // Treat both APPLIED and SCREENED as Screened stage
      if (stage.id === 'SCREENED') {
        return c.status === 'SCREENED' || c.status === 'APPLIED' || !c.status;
      }
      if (stage.id === 'EVALUATED') {
        return c.status === 'EVALUATED' || c.status === 'SHORTLISTED';
      }
      return c.status === stage.id;
    });

    const currentIndex = STAGES.findIndex(s => s.id === stage.id);
    const prevStage = currentIndex > 0 ? STAGES[currentIndex - 1] : null;
    const nextStage = currentIndex < STAGES.length - 1 ? STAGES[currentIndex + 1] : null;

    return (
      <div key={stage.id} style={{ ...styles.column, backgroundColor: stage.bg, borderColor: stage.color + '20' }}>
        <div style={styles.columnHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ ...styles.columnIndicator, backgroundColor: stage.color }} />
            <h3 style={styles.columnTitle}>{stage.label}</h3>
          </div>
          <span style={{ ...styles.columnCount, backgroundColor: stage.color + '15', color: stage.color }}>
            {stageCandidates.length}
          </span>
        </div>

        <div style={styles.cardsList}>
          {stageCandidates.length === 0 ? (
            <div style={styles.emptyColumn}>
              <span style={{ fontSize: '22px', display: 'block', marginBottom: '6px' }}>📥</span>
              <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>No candidates in {stage.label}</span>
              <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>
                Candidates update automatically as process stages complete.
              </span>
            </div>
          ) : (
            stageCandidates.map(cand => {
              const tier = getTierColor(cand.matching_percentage);
              return (
                <div 
                  key={cand.id} 
                  style={{ ...styles.card, cursor: 'pointer' }}
                  onClick={() => setSelectedCandidate(cand)}
                >
                  <div style={styles.cardHeader}>
                    <span style={{ ...styles.jobBadge, borderLeft: `3px solid ${stage.color}` }}>
                      {cand.job_title || 'General Opening'}
                    </span>
                    <span style={{ ...styles.scoreBadge, color: tier.color, backgroundColor: tier.bg }}>
                      {Math.round(cand.matching_percentage)}% Match
                    </span>
                  </div>

                  <h4 style={styles.candName}>{cand.name}</h4>
                  <p style={styles.candEmail}>{cand.email}</p>
                  {cand.phone && <p style={styles.candPhone}>{cand.phone}</p>}

                  {/* Skills summary */}
                  {cand.skills_extracted && (
                    <div style={styles.skillsContainer}>
                      {cand.skills_extracted.split(',').slice(0, 3).map((s, idx) => (
                        <span key={idx} style={styles.skillTag}>{s.trim()}</span>
                      ))}
                      {cand.skills_extracted.split(',').length > 3 && (
                        <span style={styles.skillMore}>+{cand.skills_extracted.split(',').length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={styles.title}>Candidate Pipeline</h2>
            <p style={styles.subtitle}>Track, manage, and promote candidates across the interview lifecycle from screening to hiring.</p>
          </div>
          <button 
            onClick={() => navigate('/hr-dashboard/screening')}
            style={styles.screenResumesBtn}
          >
            🔍 Screen New Resumes
          </button>
        </div>
      </header>

      {/* Advanced Filters & Search Bar */}
      <div style={styles.filtersBar}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search candidate, email, skills..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <div style={styles.filtersGroup}>
          <div style={styles.filterWrapper}>
            <label style={styles.filterLabel}>Position/Opening</label>
            <select
              value={selectedJobId}
              onChange={e => setSelectedJobId(e.target.value)}
              style={styles.selectFilter}
            >
              <option value="all">All Jobs</option>
              {uniqueJobs.map(job => (
                <option key={job.id} value={job.id}>{job.title}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterWrapper}>
            <label style={styles.filterLabel}>Match Quality</label>
            <select
              value={selectedTier}
              onChange={e => setSelectedTier(e.target.value)}
              style={styles.selectFilter}
            >
              <option value="all">All Matches</option>
              <option value="top">Top Match (≥ 85%)</option>
              <option value="strong">Strong Match (70% - 84%)</option>
              <option value="weak">Moderate/Weak (&lt; 70%)</option>
            </select>
          </div>

          {(searchQuery || selectedJobId !== 'all' || selectedTier !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedJobId('all');
                setSelectedTier('all');
              }}
              style={styles.clearBtn}
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <PipelineBoardSkeleton columns={4} />
      ) : candidates.length === 0 ? (
        <EmptyState
          icon="📊"
          title="Candidate Pipeline is Empty"
          description="Candidates screened using Gemini AI will automatically flow into these hiring stages based on their interview progress."
          actionLabel="Go to Candidate Screening"
          onAction={() => navigate('/hr-dashboard/screening')}
        />
      ) : filteredCandidates.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="No Candidates Match Filters"
          description={`None of your ${candidates.length} candidates in the pipeline match your active filters.`}
          actionLabel="Reset Filters"
          onAction={() => {
            setSearchQuery('');
            setSelectedJobId('all');
            setSelectedTier('all');
          }}
        />
      ) : (
        <div style={styles.board}>
          {STAGES.map(stage => renderColumn(stage))}
        </div>
      )}

      {/* Candidate Detail Modal */}
      {selectedCandidate && (
        <div style={styles.modalOverlay} onClick={() => setSelectedCandidate(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                <div style={styles.modalAvatar}>
                  {selectedCandidate.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 style={styles.modalTitle}>{selectedCandidate.name}</h3>
                  <p style={styles.modalSubtitle}>
                    {selectedCandidate.email} {selectedCandidate.phone && `| ${selectedCandidate.phone}`}
                  </p>
                </div>
              </div>
              <button style={styles.closeBtn} onClick={() => setSelectedCandidate(null)}>✕</button>
            </div>

            {/* Body */}
            <div style={styles.modalBody}>
              {/* Recommendation Card */}
              {selectedCandidate.ai_feedback?.overallRecommendation && (
                <div style={styles.recommendationBox}>
                  <strong style={styles.boxTitle}>Gemini Recommendation:</strong>
                  <p style={styles.boxText}>{selectedCandidate.ai_feedback.overallRecommendation}</p>
                </div>
              )}

              {/* Match Score Breakdown */}
              {selectedCandidate.ai_feedback?.breakdown && (
                <div style={styles.detailSection}>
                  <h4 style={styles.sectionTitle}>Matching Scores Breakdown</h4>
                  <div style={styles.scoresGrid}>
                    <div style={styles.scoreRow}>
                      <span style={styles.scoreLabel}>Experience Match</span>
                      <div style={styles.modalBarTrack}>
                        <div style={{ ...styles.modalBarFill, width: `${selectedCandidate.ai_feedback.breakdown.experienceScore || 0}%`, backgroundColor: '#3b82f6' }} />
                      </div>
                      <span style={styles.scorePercent}>{selectedCandidate.ai_feedback.breakdown.experienceScore || 0}%</span>
                    </div>

                    <div style={styles.scoreRow}>
                      <span style={styles.scoreLabel}>Skills Match</span>
                      <div style={styles.modalBarTrack}>
                        <div style={{ ...styles.modalBarFill, width: `${selectedCandidate.ai_feedback.breakdown.skillsScore || 0}%`, backgroundColor: '#10b981' }} />
                      </div>
                      <span style={styles.scorePercent}>{selectedCandidate.ai_feedback.breakdown.skillsScore || 0}%</span>
                    </div>

                    <div style={styles.scoreRow}>
                      <span style={styles.scoreLabel}>Education Match</span>
                      <div style={styles.modalBarTrack}>
                        <div style={{ ...styles.modalBarFill, width: `${selectedCandidate.ai_feedback.breakdown.educationScore || 0}%`, backgroundColor: '#f59e0b' }} />
                      </div>
                      <span style={styles.scorePercent}>{selectedCandidate.ai_feedback.breakdown.educationScore || 0}%</span>
                    </div>

                    <div style={styles.scoreRow}>
                      <span style={styles.scoreLabel}>Keyword & Domain Match</span>
                      <div style={styles.modalBarTrack}>
                        <div style={{ ...styles.modalBarFill, width: `${selectedCandidate.ai_feedback.breakdown.keywordScore || 0}%`, backgroundColor: '#8b5cf6' }} />
                      </div>
                      <span style={styles.scorePercent}>{selectedCandidate.ai_feedback.breakdown.keywordScore || 0}%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Strengths & Gaps */}
              <div style={styles.columnsGroup}>
                {/* Strengths */}
                <div style={styles.columnHalf}>
                  <h4 style={{ ...styles.sectionTitle, color: '#16a34a' }}>💎 Core Strengths</h4>
                  {selectedCandidate.ai_feedback?.strengths && selectedCandidate.ai_feedback.strengths.length > 0 ? (
                    <ul style={styles.bulletsList}>
                      {selectedCandidate.ai_feedback.strengths.map((str, idx) => (
                        <li key={idx} style={styles.bulletItem}>
                          <span style={styles.greenCheck}>✓</span> {str}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={styles.emptyText}>No core strengths highlighted.</p>
                  )}
                </div>

                {/* Gaps */}
                <div style={styles.columnHalf}>
                  <h4 style={{ ...styles.sectionTitle, color: '#dc2626' }}>⚠️ Areas of Improvement / Gaps</h4>
                  {selectedCandidate.ai_feedback?.gaps && selectedCandidate.ai_feedback.gaps.length > 0 ? (
                    <ul style={styles.bulletsList}>
                      {selectedCandidate.ai_feedback.gaps.map((gap, idx) => (
                        <li key={idx} style={styles.bulletItem}>
                          <span style={styles.redWarn}>⚠</span> {gap}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={styles.emptyText}>No major gaps identified.</p>
                  )}
                </div>
              </div>

              {/* Full Resume Text Collapsible/Scrollable */}
              {selectedCandidate.resume_text && (
                <div style={styles.resumeSection}>
                  <h4 style={styles.sectionTitle}>Parsed Resume Text</h4>
                  <pre style={styles.resumePre}>{selectedCandidate.resume_text}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    height: 'calc(100vh - 120px)',
    boxSizing: 'border-box'
  },
  header: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    flexShrink: 0,
    textAlign: 'left'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '800',
    color: 'var(--text-primary, #0f172a)',
    letterSpacing: '-0.02em'
  },
  subtitle: {
    margin: '6px 0 0 0',
    fontSize: '14.5px',
    color: 'var(--text-muted, #64748b)',
    lineHeight: '1.5'
  },
  loader: {
    textAlign: 'center',
    padding: '60px',
    fontSize: '16px',
    color: '#64748b',
    flex: 1
  },
  board: {
    display: 'flex',
    gap: '16px',
    overflowX: 'auto',
    flex: 1,
    paddingBottom: '16px',
    alignItems: 'stretch'
  },
  column: {
    flex: '1 0 310px',
    maxWidth: '350px',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px'
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexShrink: 0
  },
  columnIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%'
  },
  columnTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '700',
    color: '#0f172a'
  },
  columnCount: {
    fontSize: '12px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '12px'
  },
  cardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    overflowY: 'auto',
    flex: 1,
    paddingRight: '4px'
  },
  emptyColumn: {
    textAlign: 'center',
    padding: '30px 10px',
    fontSize: '13px',
    color: '#94a3b8',
    border: '2px dashed #e2e8f0',
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.3)'
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    cursor: 'default',
    textAlign: 'left'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px'
  },
  jobBadge: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#475569',
    paddingLeft: '6px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '120px'
  },
  scoreBadge: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '12px',
    flexShrink: 0
  },
  candName: {
    margin: 0,
    fontSize: '14.5px',
    fontWeight: '700',
    color: '#0f172a'
  },
  candEmail: {
    margin: 0,
    fontSize: '12.5px',
    color: '#64748b',
    wordBreak: 'break-all'
  },
  candPhone: {
    margin: 0,
    fontSize: '12px',
    color: '#64748b'
  },
  skillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '4px'
  },
  skillTag: {
    fontSize: '10px',
    fontWeight: '600',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  skillMore: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#64748b',
    padding: '2px 4px'
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    borderTop: '1px solid #f1f5f9',
    paddingTop: '10px',
    flexShrink: 0
  },
  navBtn: {
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: '#475569',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.1s ease'
  },
  navBtnNext: {
    color: '#ffffff',
    border: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  filtersBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    flexShrink: 0,
    textAlign: 'left'
  },
  searchWrapper: {
    position: 'relative',
    flex: '1 1 300px'
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    fontSize: '14px',
    pointerEvents: 'none'
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px 10px 36px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    color: '#1e293b',
    transition: 'border-color 0.15s ease'
  },
  filtersGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '16px'
  },
  filterWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  selectFilter: {
    padding: '9px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13.5px',
    fontWeight: '600',
    color: '#334155',
    backgroundColor: '#ffffff',
    outline: 'none',
    cursor: 'pointer',
    minWidth: '150px'
  },
  clearBtn: {
    padding: '10px 14px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '750px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    animation: 'modalSlideUp 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0
  },
  modalHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  modalAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    fontSize: '20px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '800',
    color: '#0f172a'
  },
  modalSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#64748b'
  },
  closeBtn: {
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '18px',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease'
  },
  modalBody: {
    padding: '24px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    textAlign: 'left'
  },
  recommendationBox: {
    backgroundColor: '#f8fafc',
    borderLeft: '4px solid #3b82f6',
    borderRadius: '0 8px 8px 0',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  boxTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1e293b'
  },
  boxText: {
    margin: 0,
    fontSize: '13.5px',
    color: '#475569',
    lineHeight: '1.5'
  },
  detailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '14.5px',
    fontWeight: '800',
    color: '#0f172a',
    letterSpacing: '-0.01em'
  },
  scoresGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  scoreRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  scoreLabel: {
    width: '180px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569'
  },
  modalBarTrack: {
    flex: 1,
    height: '10px',
    backgroundColor: '#f1f5f9',
    borderRadius: '99px',
    overflow: 'hidden'
  },
  modalBarFill: {
    height: '100%',
    borderRadius: '99px'
  },
  scorePercent: {
    width: '40px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#0f172a',
    textAlign: 'right'
  },
  columnsGroup: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap'
  },
  columnHalf: {
    flex: '1 1 280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  bulletsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  bulletItem: {
    fontSize: '13px',
    color: '#334155',
    lineHeight: '1.4',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  greenCheck: {
    color: '#16a34a',
    fontWeight: 'bold',
    flexShrink: 0
  },
  redWarn: {
    color: '#dc2626',
    fontWeight: 'bold',
    flexShrink: 0
  },
  emptyText: {
    margin: 0,
    fontSize: '12.5px',
    color: '#94a3b8',
    fontStyle: 'italic'
  },
  resumeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  resumePre: {
    margin: 0,
    padding: '16px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '12.5px',
    fontFamily: 'Consolas, Monaco, monospace',
    color: '#334155',
    whiteSpace: 'pre-wrap',
    maxHeight: '200px',
    overflowY: 'auto',
    lineHeight: '1.5'
  },
  screenResumesBtn: {
    padding: '10px 18px',
    border: 'none',
    borderRadius: '10px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '13.5px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
    transition: 'all 0.15s ease'
  }
};
